import {
  Autocomplete,
  Button,
  DatePicker,
  Dd,
  Dl,
  Dt,
  Flex,
  H1,
  H2,
  H3,
  InputMasked,
  Link,
  Section,
  Space,
  Tag,
  ToggleButton,
} from '@dnb/eufemia';
import type { DrawerListDataArrayObject } from '@dnb/eufemia/fragments/drawer-list/DrawerList';
import { trash_medium as TrashIcon } from '@dnb/eufemia/icons';
import type { ApiDto } from '@portals/shared/admin/ApiDto';
import type { ApiIncidentDto } from '@portals/shared/admin/ApiIncidentDto';
import { useAsync, useEufemiaForm, useFieldArray } from '@portals/shared-frontend/hooks';
import { keyByAndMap } from '@portals/shared-frontend/utils';
import { type JSX, useCallback, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { saveIncident } from '@/api/incidents';
import BackButton from '@/components/BackButton';
import Container from '@/components/Container';
import DeleteDialog from '@/components/DeleteDialog';
import LoadingModal from '@/components/LoadingModal';
import LoadingPage from '@/components/LoadingPage';
import MarkdownEditor from '@/components/MarkdownEditor';
import MarkdownHelperTable from '@/pages/content/components/MarkdownHelperTable';
import { request } from '@/request';

import { dtoToForm, formToDto } from './mappers';
import { DEFAULT_INCIDENT, DEFAULT_INCIDENT_UPDATE, incidentInputSchema } from './schemas';

const TIME_MASK = [/\d/, /\d/, ':', /\d/, /\d/];

function getSelectedKey(data: string | DrawerListDataArrayObject | null): string | undefined {
  return data && typeof data === 'object' && 'selectedKey' in data ? String(data.selectedKey) : undefined;
}

export default function IncidentForm(): JSX.Element {
  const { incidentId } = useParams();
  const navigate = useNavigate();

  const { data: apis, isValidating: apisLoading } = useSWR<ApiDto[]>('/api');
  const { data: incident, isValidating: incidentLoading } = useSWR<ApiIncidentDto>(
    incidentId ? `/api-incidents/${incidentId}` : null,
  );

  const {
    controller,
    controller: { setValues, getValue },
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(incidentInputSchema, DEFAULT_INCIDENT);

  const updatesController = useFieldArray(controller, 'updates');
  const apisController = useFieldArray(controller, 'apiIds');

  const apiNamesById = keyByAndMap(apis ?? [], 'id', 'name');

  const isIncident = getValue('type') === 'incident';
  const title = incident ? 'Edit incident' : 'Add incident';

  const onSubmit = handleSubmit(
    useCallback(
      async (values) => {
        await saveIncident(formToDto(values));
        navigate('..');
      },
      [navigate],
    ),
  );
  const deleteIncident = useAsync(async () => {
    await request(`/api-incidents/${incidentId}`, 'delete');
    navigate('..', { replace: true });
  }, [incidentId, navigate]);

  useEffect(() => {
    if (incident) setValues(dtoToForm(incident));
  }, [incident, setValues]);

  if (apisLoading || incidentLoading) return <LoadingPage />;

  return (
    <>
      <BackButton to="/api-incidents">Status</BackButton>
      <Container centered size="large">
        {submitting && <LoadingModal />}

        <H1 bottom={false} top="large">
          {title}
        </H1>
        <form onSubmit={onSubmit}>
          <Section backgroundColor="white" innerSpace={{ block: 'x-large' }}>
            {incidentId ? (
              apisController.items.length > 0 && (
                <Dl top="large">
                  <Dt>API</Dt>
                  <Dd>
                    <Tag text={apiNamesById[apisController.items[0]!.value]} />
                  </Dd>
                </Dl>
              )
            ) : (
              <Space top="medium">
                <Autocomplete
                  {...register.autocomplete('apiIds')}
                  data={apis?.map((api) => ({
                    content: (
                      <H3>
                        {api.name} <Link element={RouterLink} target="_blank" to={`/apis/${api.id}`} />
                      </H3>
                    ),
                    selectedKey: api.id,
                  }))}
                  label="APIs"
                  onChange={({ data }) => {
                    const selectedKey = getSelectedKey(data);
                    if (selectedKey) {
                      apisController.push(selectedKey);
                    }
                  }}
                  preventSelection
                />
                {apisController.items.length > 0 && (
                  <Tag.Group label="Selected apis" top="x-small">
                    {apisController.items.map(
                      ({ value, index, remove }) =>
                        apiNamesById[value] && (
                          <Tag key={index} onClick={remove} variant="removable">
                            {apiNamesById[value]}
                          </Tag>
                        ),
                    )}
                  </Tag.Group>
                )}
              </Space>
            )}
            <Space top="medium">
              <ToggleButton.Group label="Type" variant="radio" {...register.toggleButtonGroup('type')}>
                <ToggleButton text="Incident" value="incident" />
                <ToggleButton text="Maintenance" value="maintenance" />
              </ToggleButton.Group>
            </Space>
            <Space bottom="medium" top="medium">
              <ToggleButton.Group label="Severity" variant="radio" {...register.toggleButtonGroup('severity')}>
                <ToggleButton text="Instabilities" value="1" />
                <ToggleButton text="Reduced" value="2" />
              </ToggleButton.Group>
            </Space>
            <MarkdownEditor
              onChange={(_value) => controller.setValue('description', _value)}
              value={controller.values.description}
            />
            <MarkdownHelperTable />
          </Section>
          <Section innerSpace={{ block: 'x-large' }}>
            <H2>Timestamps</H2>
            <Space top="medium">
              <H3>{isIncident ? 'Reported at' : 'Scheduled start'}</H3>
              <Flex.Horizontal align="baseline">
                <DatePicker
                  label={isIncident ? 'Reported at date' : 'Scheduled start date'}
                  labelDirection="horizontal"
                  labelSrOnly
                  showInput
                  {...register.datePicker('reportedAtDate')}
                />
                <InputMasked
                  label={isIncident ? 'Reported at time' : 'Scheduled start time'}
                  labelDirection="horizontal"
                  labelSrOnly
                  mask={TIME_MASK}
                  placeholder="HH:mm"
                  showMask
                  size={6}
                  {...register.input('reportedAtTime')}
                />
              </Flex.Horizontal>
            </Space>
            <Space top="medium">
              <H3>{isIncident ? 'Resolved at' : 'Scheduled end'}</H3>
              <Flex.Horizontal align="baseline">
                <DatePicker
                  label={isIncident ? 'Resolved at date' : 'Scheduled end date'}
                  labelDirection="horizontal"
                  labelSrOnly
                  showInput
                  {...register.datePicker('resolvedAtDate')}
                />
                <InputMasked
                  label={isIncident ? 'Resolved at time' : 'Scheduled end time'}
                  labelDirection="horizontal"
                  labelSrOnly
                  mask={TIME_MASK}
                  placeholder="HH:mm"
                  showMask
                  size={6}
                  {...register.input('resolvedAtTime')}
                />
              </Flex.Horizontal>
            </Space>
          </Section>
          {incidentId && (
            <Section backgroundColor="white" innerSpace={{ block: 'x-large' }}>
              <H2>Updates</H2>
              {updatesController.items.map(({ index, remove }) => (
                <Space bottom="x-large" key={index} top="medium">
                  <MarkdownEditor
                    onChange={(_value) => {
                      controller.setValue(`updates.${index}.description`, _value);
                    }}
                    value={controller.values.updates?.at(index)?.description}
                  />

                  <Space top="medium">
                    <H3>Reported at</H3>
                    <Flex.Horizontal align="baseline">
                      <DatePicker
                        label="Reported at date"
                        labelDirection="horizontal"
                        labelSrOnly
                        showInput
                        {...register.datePicker(`updates.${index}.reportedAtDate`)}
                      />
                      <InputMasked
                        label="Reported at time"
                        labelDirection="horizontal"
                        labelSrOnly
                        mask={TIME_MASK}
                        placeholder="HH:mm"
                        showMask
                        size={6}
                        {...register.input(`updates.${index}.reportedAtTime`)}
                      />
                    </Flex.Horizontal>
                  </Space>
                  <Space top="medium">
                    <Button icon={TrashIcon} iconPosition="left" onClick={remove} variant="secondary">
                      Remove update
                    </Button>
                  </Space>
                </Space>
              ))}
              <Space top="medium">
                <Button
                  icon="add"
                  iconPosition="left"
                  onClick={() => updatesController.push(DEFAULT_INCIDENT_UPDATE)}
                  variant="secondary"
                >
                  Add update
                </Button>
              </Space>
            </Section>
          )}
          <Section backgroundColor="divider" innerSpace={{ block: 'medium' }}>
            <Button disabled={submitting} icon="save" iconPosition="left" right="small" text="Save" type="submit" />
            <DeleteDialog
              loading={deleteIncident.waiting}
              onDelete={deleteIncident.execute}
              text="This will permanently delete this incident and all its updates."
              title="Are you sure you want to delete this incident?"
            />
          </Section>
        </form>
      </Container>
    </>
  );
}
